import { hashKey, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { clearGatesForQuery } from '../../nats/seq-gate-store.js';

interface FetchOnceScopeState {
  activeOwners: number;
  completed: boolean;
  promise: Promise<unknown> | undefined;
}

interface ClientFetchOncePolicies {
  readonly scopes: Map<string, FetchOnceScopeState>;
  readonly listeners: Set<() => void>;
}

const policies = new WeakMap<QueryClient, ClientFetchOncePolicies>();

function clientPoliciesFor(queryClient: QueryClient): ClientFetchOncePolicies {
  let clientPolicies = policies.get(queryClient);
  if (clientPolicies === undefined) {
    clientPolicies = { scopes: new Map(), listeners: new Set() };
    policies.set(queryClient, clientPolicies);
  }
  return clientPolicies;
}

function notifyPolicyChange(queryClient: QueryClient): void {
  const clientPolicies = policies.get(queryClient);
  if (clientPolicies === undefined) return;
  for (const listener of clientPolicies.listeners) listener();
}

function queryPrefix(queryKey: QueryKey): string {
  return `${hashKey(queryKey)}:`;
}

function policyKey(queryKey: QueryKey, scope: string): string {
  return `${queryPrefix(queryKey)}${scope}`;
}

function stateFor(
  queryClient: QueryClient,
  queryKey: QueryKey,
  scope: string,
): FetchOnceScopeState {
  const clientPolicies = clientPoliciesFor(queryClient);
  const key = policyKey(queryKey, scope);
  let state = clientPolicies.scopes.get(key);
  if (state === undefined) {
    state = { activeOwners: 0, completed: false, promise: undefined };
    clientPolicies.scopes.set(key, state);
  }
  return state;
}

export function retainFetchOnceRealtimeOwner(
  queryClient: QueryClient,
  queryKey: QueryKey,
  scope: string,
  expireOnRelease = false,
): () => void {
  const state = stateFor(queryClient, queryKey, scope);
  state.activeOwners += 1;
  notifyPolicyChange(queryClient);
  return () => {
    state.activeOwners = Math.max(0, state.activeOwners - 1);
    if (expireOnRelease && state.activeOwners === 0) {
      // A cached chart is continuous only while someone receives its events.
      // A pending response also predates the next owner's concrete interest.
      state.completed = false;
      state.promise = undefined;
      if (!hasActiveFetchOnceRealtimeOwner(queryClient, queryKey)) {
        clearGatesForQuery(hashKey(queryKey));
        void queryClient.cancelQueries({ queryKey, exact: true });
      }
    }
    notifyPolicyChange(queryClient);
  };
}

export function subscribeFetchOnceRealtimePolicy(
  queryClient: QueryClient,
  listener: () => void,
): () => void {
  const clientPolicies = clientPoliciesFor(queryClient);
  clientPolicies.listeners.add(listener);
  return () => {
    clientPolicies.listeners.delete(listener);
  };
}

export function hasActiveFetchOnceRealtimeOwner(
  queryClient: QueryClient,
  queryKey: QueryKey,
): boolean {
  const clientPolicies = policies.get(queryClient);
  if (clientPolicies === undefined) return false;
  const prefix = queryPrefix(queryKey);
  for (const [key, state] of clientPolicies.scopes) {
    if (key.startsWith(prefix) && state.activeOwners > 0) return true;
  }
  return false;
}

export function hasCompletedFetchOnceRealtimeSnapshot(
  queryClient: QueryClient,
  queryKey: QueryKey,
  scope: string,
): boolean {
  return stateFor(queryClient, queryKey, scope).completed;
}

/**
 * Discard a completed fetch-once snapshot so the next
 * `runFetchOnceRealtimeSnapshot` fetches again. Used when the snapshot was
 * taken behind a deadline-forced wire gate and concrete interest attached
 * afterwards — that snapshot predates delivery and must be re-fenced.
 */
export function resetFetchOnceRealtimeSnapshot(
  queryClient: QueryClient,
  queryKey: QueryKey,
  scope: string,
): void {
  const state = stateFor(queryClient, queryKey, scope);
  state.completed = false;
  state.promise = undefined;
  notifyPolicyChange(queryClient);
}

export function markFetchOnceRealtimeSnapshotComplete(
  queryClient: QueryClient,
  queryKey: QueryKey,
  scope: string,
): void {
  if (queryClient.getQueryData(queryKey) === undefined) return;
  stateFor(queryClient, queryKey, scope).completed = true;
}

export function runFetchOnceRealtimeSnapshot<T>(
  queryClient: QueryClient,
  queryKey: QueryKey,
  scope: string,
  fetchSnapshot: () => Promise<T>,
): Promise<T> {
  const state = stateFor(queryClient, queryKey, scope);
  if (state.completed) {
    const cached = queryClient.getQueryData<T>(queryKey);
    if (cached !== undefined) return Promise.resolve(cached);
    state.completed = false;
  }
  if (state.promise !== undefined) return state.promise as Promise<T>;

  const promise = fetchSnapshot().then((value: T) => {
    if (state.promise === promise) state.completed = true;
    return value;
  });
  state.promise = promise;
  const clearPromise = (): void => {
    if (state.promise === promise) state.promise = undefined;
  };
  void promise.then(clearPromise, clearPromise);
  return promise;
}
